'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight, Mail, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

type LoginForm = { email: string; password: string; remember: boolean };
type RecoveryForm = { email: string };

type View = 'login' | 'recovery' | 'recovery-sent' | 'activation';

// Demo credentials — backend integration point: POST /api/auth/login
const DEMO_CREDENTIALS = { email: 'thomas.martin@apte-demo.fr', password: 'DemoAPTE2026' };

export default function AuthContent() {
  const [view, setView] = useState<View>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const recoveryForm = useForm<RecoveryForm>();

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    setLoginError('');
    await new Promise((r) => setTimeout(r, 1200));
    if (data.email === DEMO_CREDENTIALS.email && data.password === DEMO_CREDENTIALS.password) {
      toast.success('Connexion réussie — bienvenue dans votre espace.');
      window.location.href = '/tableau-de-bord';
    } else {
      setLoginError('Identifiants invalides — utilisez les identifiants de démonstration ci-dessous.');
    }
    setIsLoading(false);
  };

  const handleRecovery = async (data: RecoveryForm) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsLoading(false);
    setView('recovery-sent');
  };

  const fillDemo = () => {
    loginForm.setValue('email', DEMO_CREDENTIALS.email);
    loginForm.setValue('password', DEMO_CREDENTIALS.password);
    setLoginError('');
  };

  if (view === 'recovery-sent') {
    return (
      <div className="text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={28} className="text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Email envoyé</h2>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
          Si un espace personnel est associé à cette adresse, vous recevrez un lien de récupération d&apos;accès.
        </p>
        <button
          onClick={() => setView('login')}
          className="btn-outline text-sm"
        >
          Retour à la connexion
        </button>
      </div>
    );
  }

  if (view === 'recovery') {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setView('login')}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6 transition-smooth"
        >
          ← Retour à la connexion
        </button>
        <h2 className="text-2xl font-bold text-foreground mb-2">Récupérer mon accès</h2>
        <p className="text-sm text-muted-foreground mb-8">
          Indiquez l&apos;adresse email associée à votre espace personnel. Nous vous enverrons un lien de récupération.
        </p>
        <form onSubmit={recoveryForm.handleSubmit(handleRecovery)} noValidate className="space-y-5">
          <div>
            <label htmlFor="recovery-email" className="block text-sm font-semibold text-foreground mb-1.5">
              Adresse email
            </label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="recovery-email"
                type="email"
                autoComplete="email"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                  recoveryForm.formState.errors.email ? 'border-danger' : 'border-border focus:border-primary'
                }`}
                placeholder="votre@email.fr"
                {...recoveryForm.register('email', {
                  required: 'Ce champ est obligatoire',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
                })}
              />
            </div>
            {recoveryForm.formState.errors.email && (
              <p className="text-danger text-xs mt-1" role="alert">{recoveryForm.formState.errors.email.message}</p>
            )}
          </div>
          <button type="submit" className="btn-primary w-full justify-center" disabled={isLoading}>
            {isLoading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>Envoyer le lien de récupération <ArrowRight size={15} /></>
            )}
          </button>
        </form>
      </div>
    );
  }

  if (view === 'activation') {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setView('login')}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6 transition-smooth"
        >
          ← Retour à la connexion
        </button>
        <h2 className="text-2xl font-bold text-foreground mb-2">Activer mon espace personnel</h2>
        <p className="text-sm text-muted-foreground mb-8">
          Vous avez effectué une demande d&apos;orientation et souhaitez activer votre espace ? Entrez le code d&apos;activation reçu par email.
        </p>
        <div className="bg-secondary rounded-xl p-5 text-sm text-muted-foreground mb-6">
          <p className="font-semibold text-foreground mb-2">Comment obtenir mon code ?</p>
          <p>Suite à votre demande d&apos;orientation, APTE vous envoie un email d&apos;activation. Si vous ne l&apos;avez pas reçu, contactez-nous.</p>
        </div>
        <div className="flex flex-col gap-3">
          <button className="btn-primary w-full justify-center">
            J&apos;ai un code d&apos;activation <ArrowRight size={15} />
          </button>
          <Link href="/tre-accompagn" className="btn-outline w-full justify-center text-sm">
            Démarrer une demande d&apos;orientation
          </Link>
        </div>
      </div>
    );
  }

  // Default: login
  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-bold text-foreground mb-1">Connexion</h2>
      <p className="text-sm text-muted-foreground mb-8">Accédez à votre espace personnel sécurisé.</p>

      {loginError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5" role="alert">
          <AlertCircle size={16} className="text-danger mt-0.5 flex-shrink-0" />
          <p className="text-sm text-danger">{loginError}</p>
        </div>
      )}

      <form onSubmit={loginForm.handleSubmit(handleLogin)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Adresse email
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              className={`w-full pl-10 pr-4 py-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                loginForm.formState.errors.email ? 'border-danger' : 'border-border focus:border-primary'
              }`}
              placeholder="votre@email.fr"
              {...loginForm.register('email', {
                required: 'Ce champ est obligatoire',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
              })}
            />
          </div>
          {loginForm.formState.errors.email && (
            <p className="text-danger text-xs mt-1" role="alert">{loginForm.formState.errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
              Mot de passe
            </label>
            <button
              type="button"
              onClick={() => setView('recovery')}
              className="text-xs text-primary hover:underline"
            >
              Mot de passe oublié ?
            </button>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className={`w-full pl-10 pr-11 py-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                loginForm.formState.errors.password ? 'border-danger' : 'border-border focus:border-primary'
              }`}
              placeholder="Votre mot de passe"
              {...loginForm.register('password', { required: 'Ce champ est obligatoire' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {loginForm.formState.errors.password && (
            <p className="text-danger text-xs mt-1" role="alert">{loginForm.formState.errors.password.message}</p>
          )}
        </div>

        {/* Remember */}
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary"
            {...loginForm.register('remember')}
          />
          <span className="text-sm text-muted-foreground">Rester connecté sur cet appareil</span>
        </label>

        <button type="submit" className="btn-primary w-full justify-center py-3" disabled={isLoading}>
          {isLoading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>Se connecter <ArrowRight size={15} /></>
          )}
        </button>
      </form>

      {/* Demo credentials */}
      <div className="mt-5 p-4 bg-secondary rounded-xl border border-border">
        <p className="text-xs font-semibold text-foreground mb-2">Identifiants de démonstration</p>
        <div className="space-y-1.5 text-xs text-muted-foreground mb-3">
          <div className="flex items-center justify-between gap-2 font-mono bg-white rounded px-2 py-1">
            <span className="text-foreground">{DEMO_CREDENTIALS.email}</span>
          </div>
          <div className="flex items-center justify-between gap-2 font-mono bg-white rounded px-2 py-1">
            <span className="text-foreground">{DEMO_CREDENTIALS.password}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={fillDemo}
          className="text-xs font-semibold text-primary hover:text-primary/80 transition-smooth underline"
        >
          Remplir automatiquement
        </button>
      </div>

      <div className="mt-6 space-y-3">
        <div className="relative flex items-center">
          <div className="flex-1 h-px bg-border" />
          <span className="px-3 text-xs text-muted-foreground">ou</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <button
          type="button"
          onClick={() => setView('activation')}
          className="btn-outline w-full justify-center text-sm"
        >
          Activer mon espace personnel
        </button>
        <Link href="/ressources" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth py-2">
          Consulter les ressources sans connexion →
        </Link>
      </div>
    </div>
  );
}